import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Axios } from "@/utils/axios";
import { ROUTES } from "@/utils/routes";
import OtpInput from "@/components/auth/OtpInput";

const passwordRequirements =
  "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.";

const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#.^()[\]{}])[A-Za-z\d@$!%*?&#.^()[\]{}]{8,}$/;

const emailSchema = z.object({
  email: z
    .string({ required_error: "Email is required" })
    .email("Invalid email address"),
});

const otpSchema = z.object({
  otp: z
    .string({ required_error: "OTP is required" })
    .min(6, "OTP must be 6 digits")
    .max(6, "OTP must be 6 digits")
    .regex(/^\d{6}$/, "OTP must be a 6-digit number"),
});

const passwordSchema = z
  .object({
    password: z
      .string({ required_error: "Password is required" })
      .regex(passwordRegex, passwordRequirements),
    confirmPassword: z
      .string({ required_error: "Confirm password is required" })
      .min(8, "Confirm password must be at least 8 characters"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Confirm password does not match",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof emailSchema> &
  z.infer<typeof otpSchema> &
  z.infer<typeof passwordSchema>;

const ForgotPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setError,
    reset,
    clearErrors,
    formState: { errors },
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(
      step === 1 ? emailSchema : step === 2 ? otpSchema : passwordSchema
    ),
    mode: "onChange",
  });

  // Cooldown timer for resend OTP
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prevState) => !prevState);
  };

  const validateOtp = (otp: string) => {
    try {
      otpSchema.parse({ otp });
      return { isValid: true };
    } catch (error: any) {
      return { isValid: false, message: error.errors[0].message };
    }
  };

  const handleEmailSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      await Axios.post("/api/auth/forgot-password", {
        email: data.email,
      });
      toast({
        title: "OTP Sent",
        description: "A verification code has been sent to your email.",
      });
      setStep(2); // Move to OTP step
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to send OTP. Please try again.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Submit OTP
  const handleOtpSubmit = async (otp: string) => {
    setIsLoading(true);
    try {
      const otpCode = otp;
      await Axios.post("/api/auth/verify-code", {
        email: getValues("email"),
        code: otpCode,
      });
      toast({
        title: "OTP Verified",
        description: "Please set your new password.",
      });
      setStep(3);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Invalid OTP. Please try again.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
      setOtp(Array(6).fill(""));
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Submit new password
  const handlePasswordSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      await Axios.post("/api/auth/reset-password", {
        email: getValues("email"), // Use email from Step 1
        code: otp.join(""),
        newPassword: data.password,
        confirmPassword: data.confirmPassword,
      });
      toast({
        title: "Password Reset",
        description: "Your password has been successfully reset.",
      });
      await new Promise((resolve) => setTimeout(resolve, 1500));
      navigate(ROUTES.AUTH);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to reset password. Please try again.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    if (step === 1) {
      await handleEmailSubmit(data);
    } else if (step === 3) {
      await handlePasswordSubmit(data);
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    try {
      await Axios.post("/api/auth/resend-code", {
        email: getValues("email"),
        isForgotPassword: true,
      });
      setResendCooldown(30);
      toast({
        title: "New verification code sent!",
        description: "Please check your email for the new code.",
      });
    } catch (error) {
      console.error("Resend OTP error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-emerald-600">
            {step === 1 ? "Forgot Password" : "Reset Password"}
          </CardTitle>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  className="pl-10"
                  {...register("email")}
                  disabled={isLoading || step !== 1}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            {step === 2 && (
              <div className="flex flex-col justify-center">
                <h2 className="text-sm text-left font-semibold mb-4">
                  Enter Verification Code
                </h2>
                <OtpInput
                  otp={otp}
                  setOtp={setOtp}
                  onSubmit={handleOtpSubmit}
                  validate={validateOtp}
                  setError={(message) =>
                    setError("otp", { type: "manual", message })
                  }
                  clearErrors={() => clearErrors("otp")}
                  error={errors.otp?.message}
                  disabled={isLoading}
                />
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || isLoading}
                    className={`text-sm font-medium ${
                      resendCooldown > 0 || isLoading
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-emerald-600 hover:text-emerald-700"
                    }`}
                  >
                    {resendCooldown > 0
                      ? `Resend OTP in ${resendCooldown}s`
                      : "Resend OTP"}
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="******"
                      className="pl-10"
                      {...register("password")}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-2.5"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="******"
                      className="pl-10"
                      {...register("confirmPassword")}
                    />
                    <button
                      type="button"
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute right-3 top-2.5"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {"confirmPassword" in errors && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.confirmPassword?.message}
                    </p>
                  )}
                </div>
              </>
            )}

            {step !== 2 && (
              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={isLoading}
              >
                {step === 1 ? "Continue" : "Reset"}
                {isLoading && (
                  <div className="flex justify-center items-center py-12">
                    <Loader2 className="w-24 h-24 text-white-700 animate-spin" />
                  </div>
                )}
              </Button>
            )}

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/auth")}
                className="text-sm text-emerald-600 hover:text-emerald-700"
                disabled={isLoading}
              >
                Back to Sign In
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
